// Image extensions we fall back to when File.type is missing or unhelpful. Some drag
// sources (and a few browsers) hand over a File with an empty or generic MIME type,
// so the extension is the only discriminator left.
export const PREVIEWABLE_IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'avif', 'tif', 'tiff'];

export const PREVIEW_KIND_NONE = 'none';
export const PREVIEW_KIND_PDF = 'pdf';
export const PREVIEW_KIND_IMAGE = 'image';
export const PREVIEW_KIND_UNSUPPORTED = 'unsupported';

/**
 * Lowercased extension of a File-like object, or '' when there isn't one.
 * `name` is the standard File field; `path` is a react-dropzone addition, so both are
 * checked because callers pass either shape through.
 */
export function getFileExtension(file) {
    const name = file?.name || file?.path || '';
    const parts = String(name).split('.');

    return parts.length > 1 ? parts.pop().toLowerCase() : '';
}

/**
 * Decides which preview branch a file belongs to. MIME type wins when it is recognized,
 * otherwise the filename extension is consulted, and anything left over is reported as
 * unsupported so the UI can say so instead of rendering a broken <img>.
 */
export function resolvePreviewKind(file) {
    if (!file) {
        return PREVIEW_KIND_NONE;
    }

    const type = String(file.type ?? '').toLowerCase();

    if (type === 'application/pdf') {
        return PREVIEW_KIND_PDF;
    }

    if (type.startsWith('image/')) {
        return PREVIEW_KIND_IMAGE;
    }

    const extension = getFileExtension(file);

    if (extension === 'pdf') {
        return PREVIEW_KIND_PDF;
    }

    if (PREVIEWABLE_IMAGE_EXTENSIONS.includes(extension)) {
        return PREVIEW_KIND_IMAGE;
    }

    return PREVIEW_KIND_UNSUPPORTED;
}

/**
 * Normalizes whatever is currently in the page input into a real page number.
 * Anything that isn't an in-range integer (blank, letters, decimals, out of bounds)
 * resolves back to `fallbackPage`, which is what keeps the input from getting stuck
 * on NaN and keeps it in lockstep with the rendered page.
 */
export function resolvePageNumber(value, pagesNumber, fallbackPage) {
    const page = Number(value);

    if (!Number.isInteger(page) || page < 1 || page > pagesNumber) {
        return fallbackPage;
    }

    return page;
}
